/**
 * Export, import and create, for one entity kind.
 *
 * ONE COMPONENT, because three screens have now each lost these by being
 * written as their own screen: `/parts`, `/requirements` and `/states` replaced
 * the generic EntityList, and none of them inherited what it offered. States
 * went from a table with a New button to a tree with none, and the API could
 * create a sub-state for weeks while nothing on screen could (issue #42). A
 * screen that renders this gets all three, gated the same way.
 */

import { useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import CreateEntityDialog from './CreateEntityDialog'
import CreateRequirementDialog from './CreateRequirementDialog'
import CreateVerificationDialog from './CreateVerificationDialog'
import CreateStateDialog from '../states/CreateStateDialog'
import ExportMenu from './ExportMenu'
import ImportCsvDialog from './ImportCsvDialog'
import type { KindSchema } from '../../schema'
import { useCanWrite } from '../../state/AppData'

export default function EntityActions({
  kind,
  onCreated,
  onImported,
  shownIds,
  filtered,
}: {
  kind: KindSchema
  onCreated: (identifier: string) => void
  onImported: () => void
  /**
   * The ids of the rows the host's table is showing, if it knows - which turns
   * on the export menu's "the N shown" items. Optional because the hosts that
   * use this (EntityList, States) hold the table's rows inside a child or a
   * tree and have no such list; they get the "All" item, which is the
   * honest menu for them.
   */
  shownIds?: string[] | null
  filtered?: boolean
}) {
  const { allowed, reason } = useCanWrite()
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)

  const created = (identifier: string) => {
    setCreating(false)
    onCreated(identifier)
  }

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', flexShrink: 0 }}>
      {/* Export and import together: the round trip through a spreadsheet
          is one workflow, and separating the halves is how people end up
          editing an export they have no way to bring back.

          Neither is gated on `can_create`. Export is a read, and an import of
          a kind BEST cannot state from nothing is still a legitimate bulk
          EDIT - the server refuses the rows that would be creations, one row
          at a time, and says so in the preview. Hiding the button would
          refuse the whole file for the sake of the rows it could not add. */}
      <ExportMenu plural={kind.plural} shownIds={shownIds} filtered={filtered} />
      <Tooltip title={allowed ? '' : reason} describeChild>
        <span>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setImporting(true)}
            disabled={!allowed}
          >
            Import CSV
          </Button>
        </span>
      </Tooltip>
      {kind.can_create && (
        <Tooltip title={allowed ? '' : reason} describeChild>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreating(true)}
              disabled={!allowed}
            >
              New
            </Button>
          </span>
        </Tooltip>
      )}

      {creating &&
        // Requirements get their own dialog: the ID and the package FOLLOW
        // from the level and scoping, which is worth asking for in that order
        // rather than as a generic list of fields.
        (kind.name === 'requirement' ? (
          <CreateRequirementDialog kind={kind} onClose={() => setCreating(false)} onCreated={created} />
        ) : kind.name === 'state' ? (
          // States too (issue #50): a mode or phase, who exhibits it, where
          // its machine starts, and its transitions - none a generic field.
          <CreateStateDialog onClose={() => setCreating(false)} onCreated={created} />
        ) : kind.name === 'verification' ? (
          // Cases, plans and campaigns (issue #106): the kind decides the
          // fields and the parents, so it is asked first.
          <CreateVerificationDialog kind={kind} onClose={() => setCreating(false)} onCreated={created} />
        ) : (
          <CreateEntityDialog kind={kind} onClose={() => setCreating(false)} onCreated={created} />
        ))}

      {importing && (
        <ImportCsvDialog kind={kind} onClose={() => setImporting(false)} onImported={onImported} />
      )}
    </Stack>
  )
}
